import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';

import { feel } from '@kilat/ui';
import { AGENT_LESSONS, agentProgress, type AgentLesson } from '@kilat/shared';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { academyRepoFor } from '../lib/repositories/academy-repo.js';

/**
 * Agent field training (gap G18).
 *
 * The agent PWA has twelve routes and every one of them is money or admin. For
 * a brand-new agent with no signups, every screen renders zero — the product is
 * a money mirror that reflects failure back at the person we most need to
 * persist. This is the one screen that has something in it on day one, and the
 * one place progress moves before any commission does.
 *
 * Design decisions that follow from who reads this and where:
 *
 *   - **Everything is open from the start.** No sequencing, no locks. An agent
 *     who just got rejected outside a shop needs the rejection lesson NOW, not
 *     after finishing lesson three.
 *   - **The objection drills show the exact words.** A strategy — "acknowledge,
 *     then reframe" — is useless to someone standing in front of an owner who
 *     has just said no. The reply is written out to be repeated verbatim.
 *   - **Every lesson ends with one thing to do.** A lesson that ends without a
 *     next action is a dead end, and a screen that tells a new agent nothing to
 *     do is how the app becomes something they stop opening.
 *   - **Marking done is self-report.** No quiz. The goal is that they read it
 *     before the first visit, and a test would add friction exactly where we
 *     are trying to remove it.
 */
export function AcademyPage() {
  const t = useI18n();
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  const [completed, setCompleted] = useState<ReadonlySet<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const progress = await academyRepoFor(uid).get('agent');
        if (!cancelled) setCompleted(new Set(progress?.completedLessonIds ?? []));
      } catch (e: unknown) {
        logger.warn('agent academy progress unavailable', { error: String(e) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const progress = useMemo(() => agentProgress(completed), [completed]);

  // Open the next unread lesson by default, so an agent who opens this between
  // visits lands on something to read rather than a list to choose from.
  useEffect(() => {
    if (!loading && openId === null) setOpenId(progress.next?.id ?? AGENT_LESSONS[0]?.id ?? null);
  }, [loading, openId, progress.next]);

  const markDone = useCallback(
    async (lessonId: string) => {
      if (!uid) return;
      feel('success');
      const next = new Set(completed);
      next.add(lessonId);
      setCompleted(next);
      setSaveError(null);
      try {
        await academyRepoFor(uid).set('agent', { completedLessonIds: [...next] });
      } catch (e: unknown) {
        logger.error('agent academy save failed', { error: String(e), lessonId });
        setCompleted(completed);
        setSaveError(t.t('agentAcademy.saveFailed'));
      }
    },
    [uid, completed, t],
  );

  if (loading) {
    return (
      <main style={pageStyle} aria-busy>
        <p style={eyebrowStyle}>{t.t('agentAcademy.eyebrow')}</p>
        <h1 style={titleStyle}>{t.t('agentAcademy.title')}</h1>
        <p style={mutedStyle}>{t.t('agentAcademy.loading')}</p>
      </main>
    );
  }

  return (
    <main style={pageStyle}>
      <p style={eyebrowStyle}>{t.t('agentAcademy.eyebrow')}</p>
      <h1 style={titleStyle}>{t.t('agentAcademy.title')}</h1>
      <p style={mutedStyle}>{t.t('agentAcademy.subtitle')}</p>

      <div style={progressWrapStyle}>
        <div style={progressTrackStyle} aria-hidden>
          <div style={{ ...progressFillStyle, width: `${Math.round(progress.fraction * 100)}%` }} />
        </div>
        <p style={progressLabelStyle}>
          {t.t('agentAcademy.progress', { done: progress.done, total: progress.total })}
        </p>
      </div>

      {progress.certified ? (
        <section style={certifiedStyle}>
          <h2 style={certifiedTitleStyle}>{t.t('agentAcademy.certifiedTitle')}</h2>
          <p style={{ margin: 0 }}>{t.t('agentAcademy.certifiedBody')}</p>
          <span style={badgeStyle}>{t.t('agentAcademy.badge')}</span>
        </section>
      ) : null}

      {saveError ? (
        <p role="alert" style={errorStyle}>
          {saveError}
        </p>
      ) : null}

      <ul style={listStyle}>
        {AGENT_LESSONS.map((lesson) => (
          <LessonCard
            key={lesson.id}
            lesson={lesson}
            done={completed.has(lesson.id)}
            open={openId === lesson.id}
            isNext={!completed.has(lesson.id) && progress.next?.id === lesson.id}
            onToggle={() => {
              feel('tap');
              setOpenId(openId === lesson.id ? null : lesson.id);
            }}
            onDone={() => void markDone(lesson.id)}
          />
        ))}
      </ul>
    </main>
  );
}

function LessonCard({
  lesson,
  done,
  open,
  isNext,
  onToggle,
  onDone,
}: {
  readonly lesson: AgentLesson;
  readonly done: boolean;
  readonly open: boolean;
  readonly isNext: boolean;
  readonly onToggle: () => void;
  readonly onDone: () => void;
}) {
  const t = useI18n();
  return (
    <li style={cardStyle}>
      <button type="button" style={cardHeaderStyle} onClick={onToggle} aria-expanded={open}>
        <span style={{ fontWeight: 600, textAlign: 'left' }}>{t.t(lesson.titleKey)}</span>
        <span style={statusStyle}>
          {done ? t.t('agentAcademy.done') : isNext ? t.t('agentAcademy.nextUp') : null}
        </span>
      </button>

      {open ? (
        <div style={bodyWrapStyle}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>{t.t(lesson.bodyKey)}</p>

          {lesson.drills?.length ? (
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <h3 style={drillsHeadingStyle}>{t.t('agentAcademy.drillsHeading')}</h3>
              {lesson.drills.map((d) => (
                <div key={d.objectionKey} style={drillStyle}>
                  <p style={objectionStyle}>{t.t(d.objectionKey)}</p>
                  {/* The exact words, not a strategy. */}
                  <p style={{ margin: 0, lineHeight: 1.6 }}>{t.t(d.replyKey)}</p>
                </div>
              ))}
            </div>
          ) : null}

          <div style={actionStyle}>
            <span style={actionLabelStyle}>{t.t('agentAcademy.yourMove')}</span>
            <p style={{ margin: 0 }}>{t.t(lesson.actionKey)}</p>
          </div>

          {done ? (
            <span style={statusStyle}>{t.t('agentAcademy.done')}</span>
          ) : (
            <button type="button" className="k-press" style={doneButtonStyle} onClick={onDone}>
              {t.t('agentAcademy.markDone')}
            </button>
          )}
        </div>
      ) : null}
    </li>
  );
}

const pageStyle: CSSProperties = {
  padding: 'var(--space-5) var(--space-4) var(--space-16)',
  display: 'grid',
  gap: 'var(--space-3)',
};

const eyebrowStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-primary)',
};

const titleStyle: CSSProperties = { margin: 0, fontSize: 'var(--text-h1)' };
const mutedStyle: CSSProperties = { margin: 0, color: 'var(--color-on-surface-muted)' };

const progressWrapStyle: CSSProperties = { display: 'grid', gap: 'var(--space-2)' };
const progressTrackStyle: CSSProperties = {
  height: 8,
  borderRadius: 999,
  background: 'var(--color-border)',
  overflow: 'hidden',
};
const progressFillStyle: CSSProperties = {
  height: '100%',
  background: 'var(--color-primary)',
  transition: 'width 300ms ease',
};
const progressLabelStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: 'var(--space-3)',
};

const cardStyle: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-1)',
  overflow: 'hidden',
};

const cardHeaderStyle: CSSProperties = {
  width: '100%',
  minHeight: 'var(--touch-target)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-4)',
  background: 'transparent',
  border: 'none',
  fontSize: 'var(--text-body)',
  fontFamily: 'inherit',
  color: 'var(--color-on-surface)',
  cursor: 'pointer',
};

const statusStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  whiteSpace: 'nowrap',
};

const bodyWrapStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
  padding: '0 var(--space-4) var(--space-4)',
};

const drillsHeadingStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--color-on-surface-muted)',
};

const drillStyle: CSSProperties = {
  display: 'grid',
  gap: 4,
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-2, var(--color-surface))',
  border: '1px solid var(--color-border)',
};

const objectionStyle: CSSProperties = { margin: 0, fontWeight: 600 };

const actionStyle: CSSProperties = {
  display: 'grid',
  gap: 4,
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--tint-subtle, rgba(13,122,79,0.06))',
};

const actionLabelStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--color-primary)',
};

const doneButtonStyle: CSSProperties = {
  minHeight: 'var(--touch-target)',
  padding: '0 var(--space-5)',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontWeight: 600,
  fontSize: 'var(--text-body)',
  fontFamily: 'inherit',
  cursor: 'pointer',
  justifySelf: 'start',
};

const certifiedStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--tint-subtle, rgba(13,122,79,0.08))',
  border: '1px solid var(--color-primary)',
};

const certifiedTitleStyle: CSSProperties = { margin: 0, fontSize: 'var(--text-h2)' };

const badgeStyle: CSSProperties = {
  justifySelf: 'start',
  padding: '4px var(--space-3)',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontSize: 'var(--text-meta)',
  fontWeight: 700,
};

const errorStyle: CSSProperties = { margin: 0, color: 'var(--color-warning, #b45309)' };
